import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { encodeReceipt } from '../utils/escpos';

const ThermalPrinterContext = createContext(null);

const DEFAULT_BAUD_RATE = 9600;

function isWebSerialSupported() {
  return typeof navigator !== 'undefined' && 'serial' in navigator;
}

async function closePortIfOpen(port) {
  if (!port) return;
  try {
    if (port.readable || port.writable) {
      await port.close();
    }
  } catch {
    // Port may already be closed.
  }
}

async function openPort(port, baudRate = DEFAULT_BAUD_RATE) {
  await closePortIfOpen(port);
  await port.open({ baudRate });
}

export function ThermalPrinterProvider({ children }) {
  const portRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');

  const isSupported = isWebSerialSupported();

  const syncConnected = useCallback(() => {
    const port = portRef.current;
    setConnected(!!port?.readable && !!port?.writable);
  }, []);

  useEffect(() => {
    if (!isSupported) return undefined;

    let cancelled = false;

    (async () => {
      try {
        const ports = await navigator.serial.getPorts();
        if (!ports.length || cancelled) return;
        await openPort(ports[0]);
        if (cancelled) {
          await closePortIfOpen(ports[0]);
          return;
        }
        portRef.current = ports[0];
        syncConnected();
      } catch {
        if (!cancelled) {
          portRef.current = null;
          setConnected(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isSupported, syncConnected]);

  const connect = useCallback(async () => {
    if (!isSupported) {
      const message = 'Direct USB printing needs Chrome or Edge on desktop.';
      setError(message);
      throw new Error(message);
    }

    setConnecting(true);
    setError('');
    try {
      const port = await navigator.serial.requestPort();
      await openPort(port);
      portRef.current = port;
      syncConnected();
    } catch (err) {
      portRef.current = null;
      setConnected(false);
      const message = err?.message || 'Could not connect to the printer.';
      setError(message);
      throw err;
    } finally {
      setConnecting(false);
    }
  }, [isSupported, syncConnected]);

  const disconnect = useCallback(async () => {
    setError('');
    await closePortIfOpen(portRef.current);
    portRef.current = null;
    setConnected(false);
  }, []);

  const printBytes = useCallback(async (bytes) => {
    let port = portRef.current;

    if (!port?.writable) {
      if (!isSupported) throw new Error('Printer not connected.');
      const ports = await navigator.serial.getPorts();
      if (!ports.length) throw new Error('Printer not connected.');
      port = ports[0];
      await openPort(port);
      portRef.current = port;
      syncConnected();
    }

    const writer = port.writable.getWriter();
    try {
      await writer.write(bytes);
    } finally {
      writer.releaseLock();
    }
  }, [isSupported, syncConnected]);

  const printReceipt = useCallback(async (receipt, options = {}) => {
    const bytes = await encodeReceipt(receipt, options);
    await printBytes(bytes);
  }, [printBytes]);

  const value = useMemo(() => ({
    isSupported,
    connected,
    connecting,
    error,
    connect,
    disconnect,
    printReceipt,
    clearError: () => setError(''),
  }), [isSupported, connected, connecting, error, connect, disconnect, printReceipt]);

  return (
    <ThermalPrinterContext.Provider value={value}>
      {children}
    </ThermalPrinterContext.Provider>
  );
}

export function useThermalPrinter() {
  const ctx = useContext(ThermalPrinterContext);
  if (!ctx) throw new Error('useThermalPrinter must be used within ThermalPrinterProvider');
  return ctx;
}
